import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

interface HeroCarouselProps {
  slides: Movie[];
}

const hotKeywords = ["深海回声", "长安夜行录", "星轨之外", "看片APP"];

export default function HeroCarousel({ slides }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused, slides.length]);

  const current = slides[active];

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === active ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.banner ?? slide.cover}
              alt={`${slide.title}焦点海报`}
              title={`${slide.title} - 看片免费app网站推荐`}
              className="w-full h-full object-cover object-center"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/25"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/40"></div>
      </div>

      <div className="relative w-full max-w-page mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center gap-10 py-14 md:py-20 min-h-[500px] md:min-h-[560px]">
          <div className="flex-1 max-w-2xl">
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] text-accent-300 font-label uppercase">
              <span className="w-6 h-px bg-accent-500"></span>
              Free Movie Portal
            </span>
            <h1 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.25] text-background-50">
              免费看高清影视
              <br />
              <span className="text-accent-400">好片与看片工具</span>一站找齐
            </h1>
            <p className="mt-5 text-sm md:text-base leading-7 text-primary-100 max-w-xl">
              看片免费app网站精选电影、电视剧与综艺资源介绍，同步整理热门看片应用。
              按分类筛选、按评分排序，十分钟找到今晚想看的那一部。
            </p>

            <form
              onSubmit={handleSearch}
              className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 max-w-xl rounded-lg sm:rounded-full bg-background-50/95 p-2"
            >
              <div className="flex items-center gap-2 flex-1 px-3">
                <i className="ri-search-line text-foreground-400"></i>
                <input
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="输入片名、演员或 APP 名称"
                  aria-label="搜索影片或应用"
                  className="w-full bg-transparent py-2 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-2.5 rounded-md sm:rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                开始搜索
              </button>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-primary-200">
              <span className="text-primary-300">热搜：</span>
              {hotKeywords.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="px-2.5 py-1 rounded-full border border-primary-700/80 text-primary-100 hover:border-accent-400 hover:text-accent-300 transition-colors"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-[360px] shrink-0">
            <div className="rounded-lg border border-primary-700/60 bg-primary-900/70 backdrop-blur-sm p-5">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[11px] text-accent-300 font-label tracking-wider">
                  <i className="ri-fire-line"></i>
                  本期焦点
                </span>
                <span className="text-[11px] text-primary-300 font-label">
                  {String(active + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
                </span>
              </div>

              <Link to={`/detail/${current.id}`} className="mt-4 flex gap-4 cursor-pointer group">
                <div className="w-[86px] h-[126px] rounded-md overflow-hidden shrink-0 bg-primary-800">
                  <img
                    src={current.cover}
                    alt={`${current.title}封面`}
                    title={`${current.title} - 影视详情`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex flex-col">
                  <h2 className="text-lg font-semibold text-background-50 group-hover:text-accent-400 transition-colors truncate">
                    {current.title}
                  </h2>
                  <p className="mt-1 text-xs text-primary-300 truncate">
                    {current.year} · {current.region} · {current.genres.join(" / ")}
                  </p>
                  <p className="mt-2 text-xs text-accent-400 flex items-center gap-1 font-label">
                    <i className="ri-star-fill"></i>
                    {current.rating.toFixed(1)}
                    <span className="text-primary-400 ml-2">{current.heat}</span>
                  </p>
                  <p className="mt-2 text-xs text-primary-200 leading-5 line-clamp-3">{current.tagline}</p>
                </div>
              </Link>

              <div className="mt-4 flex items-center gap-2">
                <Link
                  to={`/detail/${current.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-accent-500 text-primary-950 text-xs font-medium whitespace-nowrap hover:bg-accent-400 transition-colors"
                >
                  <i className="ri-play-circle-line"></i>
                  查看详情
                </Link>
                <Link
                  to="/app"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-primary-600 text-primary-100 text-xs whitespace-nowrap hover:border-accent-400 hover:text-accent-300 transition-colors"
                >
                  <i className="ri-app-store-line"></i>
                  找看片APP
                </Link>
              </div>

              <div className="mt-4 flex items-center gap-2">
                {slides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    onClick={() => setActive(index)}
                    aria-label={`切换到${slide.title}`}
                    className={`h-1.5 rounded-full transition-all cursor-pointer ${
                      index === active ? "w-8 bg-accent-500" : "w-3 bg-primary-600 hover:bg-primary-500"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}